import { Navigate } from 'react-router-dom';
import { useWorkspaceContext, useIsOwner } from './WorkspaceProvider';

/**
 * Route guard that redirects MEMBERs away from owner-only pages.
 *
 * Pages wrapped in this guard:
 *   /dashboard/settings/billing
 *   /dashboard/settings/usage          (the page lives at /dashboard/usage)
 *   /dashboard/settings/members
 *   /dashboard/settings/connections
 *   /dashboard/settings/connected-apps
 *
 * The sidebar already hides these for MEMBERs, but the route guard is the
 * defense-in-depth layer that handles direct URL navigation, browser
 * back/forward, and pasted links.
 *
 * IMPORTANT: while the workspace context is still loading we render
 * `null` instead of redirecting. Without that, an OWNER who refreshes the
 * billing page would briefly see `activeWorkspace === null` (loading) →
 * `useIsOwner() === false` → bounced to /dashboard before the workspace
 * data arrives. The brief moment of nothing is preferable to that race.
 */
export function OwnerOnlyRoute({ children }: { children: React.ReactNode }) {
  const { activeWorkspace, isLoading } = useWorkspaceContext();
  const isOwner = useIsOwner();

  if (isLoading || !activeWorkspace) {
    return null;
  }
  if (!isOwner) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}
